import {
  ParametrageGeneralDac,
  ParametrageGeneralDacDto,
  ParamGeneralDacBatchDto,
  IResponsePaginateRaw,
  WithMetadata,
  ParamGeneralDacValue,
  ParamGeneralDacPlacement,
  ParamGeneralDacPlacementRequest,
  ParamGeneralDacPlacementReorderItem,
  ParamGeneralDacMixedReorderItem,
  ParamGeneralDacPlacementUpdateRequest,
} from '@/types';
import { DynaDocsModel } from '@/types/dyna-docs-model.types';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BaseQueryParams, IResponse } from '@/types/generic-responses.types';

@Injectable({
  providedIn: 'root',
})
export class DacGeneralParamsService {
  BASEURL = `${environment.api.dacUrl}/param-general-dac`;
  BASEURL_VALUES = `${environment.api.dacUrl}/param-general-dac-values`;
  PARAMS_BASEURL = `${environment.api.paramsUrl}`;
  httpClient = inject(HttpClient);

  getAll(params?: BaseQueryParams): Observable<IResponsePaginateRaw<ParametrageGeneralDac>> {
    return this.httpClient.get<IResponsePaginateRaw<ParametrageGeneralDac>>(`${this.BASEURL}`, {
      params: params as HttpParams,
    });
  }

  getAllList(): Observable<IResponse<ParametrageGeneralDac>> {
    return this.httpClient.get<IResponse<ParametrageGeneralDac>>(`${this.BASEURL}/list`);
  }

  getOne(id: number): Observable<WithMetadata<ParametrageGeneralDac>> {
    return this.httpClient.get<WithMetadata<ParametrageGeneralDac>>(`${this.BASEURL}/${id}`);
  }

  getDynaDocsModels(): Observable<DynaDocsModel[]> {
    return this.httpClient.get<DynaDocsModel[]>(`${this.PARAMS_BASEURL}/dyna-docs-models/active-list`);
  }

  getByDynaDocsModelId(dynaDocsModelId: number): Observable<ParametrageGeneralDac[]> {
    return this.httpClient.get<ParametrageGeneralDac[]>(`${this.BASEURL}/by-dyna-docs-model/${dynaDocsModelId}`);
  }

  add(dto: ParametrageGeneralDacDto): Observable<WithMetadata<ParametrageGeneralDac>> {
    return this.httpClient.post<WithMetadata<ParametrageGeneralDac>>(`${this.BASEURL}`, dto);
  }

  addBatch(dtos: ParamGeneralDacBatchDto[]): Observable<WithMetadata<ParametrageGeneralDac>[]> {
    return this.httpClient.post<WithMetadata<ParametrageGeneralDac>[]>(`${this.BASEURL}/batch`, dtos);
  }

  update(id: number, dto: ParametrageGeneralDacDto): Observable<WithMetadata<ParametrageGeneralDac>> {
    return this.httpClient.put<WithMetadata<ParametrageGeneralDac>>(`${this.BASEURL}/${id}`, dto);
  }

  updateBatch(dtos: ParamGeneralDacBatchDto[]): Observable<WithMetadata<ParametrageGeneralDac>[]> {
    return this.httpClient.put<WithMetadata<ParametrageGeneralDac>[]>(`${this.BASEURL}/batch`, dtos);
  }

  getByDac(dacId: number, dacVer: number): Observable<ParamGeneralDacValue[]> {
    return this.httpClient.get<ParamGeneralDacValue[]>(`${this.BASEURL_VALUES}/${dacId}/${dacVer}`);
  }

  // --- Section-level placement (global variable <-> one document dynamique) ---

  getPlacementsByDynaDocs(dynaDocsId: number, dynaDocsVer: number): Observable<ParamGeneralDacPlacement[]> {
    return this.httpClient.get<ParamGeneralDacPlacement[]>(`${this.BASEURL}/placements/by-dyna-docs/${dynaDocsId}/${dynaDocsVer}`);
  }

  addPlacement(dto: ParamGeneralDacPlacementRequest): Observable<ParamGeneralDacPlacement> {
    return this.httpClient.post<ParamGeneralDacPlacement>(`${this.BASEURL}/placements`, dto);
  }

  reorderPlacements(items: ParamGeneralDacPlacementReorderItem[]): Observable<void> {
    return this.httpClient.put<void>(`${this.BASEURL}/placements/reorder`, { items });
  }

  removePlacement(placementId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.BASEURL}/placements/${placementId}`);
  }

  // Overrides (or, sent blank, clears) one placement's own description.
  updatePlacement(placementId: number, dto: ParamGeneralDacPlacementUpdateRequest): Observable<ParamGeneralDacPlacement> {
    return this.httpClient.put<ParamGeneralDacPlacement>(`${this.BASEURL}/placements/${placementId}`, dto);
  }

  // Reorders a section's whole field list (local fields + shared placements) as one sequence,
  // so the two can be freely interleaved instead of each keeping its own separate order.
  reorderMixed(dynaDocsId: number, dynaDocsVer: number, items: ParamGeneralDacMixedReorderItem[]): Observable<void> {
    return this.httpClient.put<void>(`${this.BASEURL}/placements/dyna-docs/${dynaDocsId}/${dynaDocsVer}/reorder-mixed`, { items });
  }

  // Excel export of every shared/global variable (active and inactive) with the sections
  // it's actually placed in - lets you audit the library for forgotten or duplicate entries.
  exportToExcel(): Observable<Blob> {
    return this.httpClient.get(`${this.BASEURL}/export`, { responseType: 'blob' }).pipe(
      tap((blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'variables_globales.xlsx';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
    );
  }
}
